import * as Ct from "ldlj"
import { useDispatch } from "react-redux"
import { useIntl } from "react-intl"
import { useParams } from "react-router-dom"
import { useEffect, useState } from "react"
import styled from "styled-components/macro"
import { getIdFromParams } from "../../utils/company"
import {
  Account,
  createAccountResetAction,
  getEuConstructionVatAccountSettingsThunk,
  vatType,
} from "../../store/ducks/companySettings.ducks"
import { Select } from "../Commons/Select"
import { useRNBSelector } from "../../store/rootReducer"
import { ReactComponent as Plus } from "../../assets/plus.svg"
import {
  CreateEuConstructionVatModal,
  EuConstructionVatAccountPayload,
} from "./CreateEuConstructionVatModal"
import { ReactComponent as Eye } from "../../assets/eyeFilled.svg"
import { colors } from "../../styles/design.config"
import { EuConstructionHistoryModal } from "./EuConstructionHistoryModal"
import { CompanyStub } from "../../store/ducks/companies.ducks"

/* eslint-disable camelcase */
export interface EuConstructionVatAccountsState {
  constructionVatAccount: Account | null
  constructionReverseVatAccount: Account | null
  euVatAccount: Account | null
  euReverseVatAccount: Account | null
}

interface EuConstructionVatAccountProps {
  onAccountsUpdated: ({
    disabled,
    euConstructionVatAccounts,
  }: {
    disabled: boolean
    euConstructionVatAccounts: EuConstructionVatAccountsState
  }) => void
}

export const EuConstructionVatAccounts = ({
  onAccountsUpdated,
}: EuConstructionVatAccountProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const {
    createdEuConstructionVatAccount,
    construction_vat,
    construction_reverse_vat,
    eu_vat,
    eu_reverse_vat,
    selectedAccounts,
  } = useRNBSelector((state) => state.companySettings)
  const companySettings = useRNBSelector((state) => state.companySettings)

  const selectedCompanyId = getIdFromParams(useParams())("company_id") || 0
  const company = useRNBSelector(
    (state) => state.companies.companies[selectedCompanyId] || CompanyStub
  )
  const companyNotOnboarded =
    company.buy_third_party_account_id === null &&
    company.sell_third_party_account_id === null &&
    company.auxiliary === null

  useEffect(() => {
    dispatch(
      getEuConstructionVatAccountSettingsThunk({
        companyId: selectedCompanyId,
      })
    )
  }, [dispatch, selectedCompanyId])

  useEffect(() => {
    if (createdEuConstructionVatAccount) {
      const vatType = createdEuConstructionVatAccount.vatType
      const newEuConstructionVatAccounts =
        vatType === "construction_vat"
          ? {
              ...accounts,
              constructionVatAccount: createdEuConstructionVatAccount,
            }
          : vatType === "construction_reverse_vat"
          ? {
              ...accounts,
              constructionReverseVatAccount: createdEuConstructionVatAccount,
            }
          : vatType === "eu_vat"
          ? {
              ...accounts,
              euVatAccount: createdEuConstructionVatAccount,
            }
          : {
              ...accounts,
              euReverseVatAccount: createdEuConstructionVatAccount,
            }
      setAccounts(newEuConstructionVatAccounts)
      dispatch(createAccountResetAction())
    }
  }, [createdEuConstructionVatAccount])

  const [accounts, setAccounts] = useState<EuConstructionVatAccountsState>({
    constructionVatAccount: null,
    constructionReverseVatAccount: null,
    euVatAccount: null,
    euReverseVatAccount: null,
  })
  const [initialAccounts, setInitialAccounts] =
    useState<EuConstructionVatAccountsState>({
      constructionVatAccount: null,
      constructionReverseVatAccount: null,
      euVatAccount: null,
      euReverseVatAccount: null,
    })

  const accountsOptions = (vatType: vatType) => {
    return companySettings[vatType].possible_accounts
      .map((account) => ({
        value: account.id.toString(),
        label: `${account.number} ${account.details}`,
      }))
      .filter(
        (option) =>
          !selectedAccounts.some(
            (selectedAccount) =>
              selectedAccount.accountId === Number(option.value)
          )
      )
  }

  const findSelectedOptionValue = (vatType: vatType) => {
    const emptyOption = {
      value: "",
      label: "",
    }
    switch (vatType) {
      case "eu_vat":
        const euVatAccount = accounts.euVatAccount
        return euVatAccount
          ? {
              value: euVatAccount.id.toString(),
              label: `${euVatAccount.number} ${euVatAccount.details}`,
            }
          : emptyOption
      case "eu_reverse_vat":
        const euReverseVatAccount = accounts.euReverseVatAccount
        return euReverseVatAccount
          ? {
              value: euReverseVatAccount.id.toString(),
              label: `${euReverseVatAccount.number} ${euReverseVatAccount.details}`,
            }
          : emptyOption
      case "construction_vat":
        const constructionVatAccount = accounts.constructionVatAccount
        return constructionVatAccount
          ? {
              value: constructionVatAccount.id.toString(),
              label: `${constructionVatAccount.number} ${constructionVatAccount.details}`,
            }
          : emptyOption
      case "construction_reverse_vat":
        const constructionReverseVatAccount =
          accounts.constructionReverseVatAccount
        return constructionReverseVatAccount
          ? {
              value: constructionReverseVatAccount.id.toString(),
              label: `${constructionReverseVatAccount.number} ${constructionReverseVatAccount.details}`,
            }
          : emptyOption
    }
  }

  useEffect(() => {
    const accounts = {
      constructionVatAccount: construction_vat.selected_account,
      constructionReverseVatAccount: construction_reverse_vat.selected_account,
      euVatAccount: eu_vat.selected_account,
      euReverseVatAccount: eu_reverse_vat.selected_account,
    }
    setAccounts(accounts)
    setInitialAccounts(accounts)
  }, [
    construction_vat.selected_account,
    construction_reverse_vat.selected_account,
    eu_vat.selected_account,
    eu_reverse_vat.selected_account,
  ])

  const [isModalDisplayed, setIsModalDisplayed] = useState(false)
  const [
    euConstructionVatAccountToCreate,
    setEuConstructionVatAccountToCreate,
  ] = useState<EuConstructionVatAccountPayload | null>(null)
  const [historyDisplay, setHistoryDisplay] = useState<{
    isDisplayed: boolean
    vatType: vatType | null
  }>({
    isDisplayed: false,
    vatType: null,
  })

  useEffect(() => {
    const accountsChanged =
      JSON.stringify(accounts) !== JSON.stringify(initialAccounts)

    const disabled = !accountsChanged

    onAccountsUpdated({
      euConstructionVatAccounts: accounts,
      disabled,
    })
  }, [accounts])

  return (
    <>
      <EuConstructionHistoryModal
        isDisplayed={historyDisplay.isDisplayed}
        onClose={() => {
          setHistoryDisplay({
            isDisplayed: false,
            vatType: null,
          })
        }}
        vatType={historyDisplay.vatType}
      />
      <CreateEuConstructionVatModal
        isDisplayed={isModalDisplayed}
        onClose={() => {
          setIsModalDisplayed(false)
          setEuConstructionVatAccountToCreate(null)
        }}
        euConstructionVatAccountToCreate={
          euConstructionVatAccountToCreate || {
            vatType: null,
            prefix: [],
            forbid: [],
          }
        }
      />
      <Container>
        <Ct.Text
          text={intl.formatMessage({
            id: "office-company-settings.auto-vat-accounts-info",
          })}
          textStyle={{ lineHeight: 3 }}
        />
        <Ct.Spacer height={2} />
        <BorderedContainer>
          <>
            {[
              "eu_vat",
              "eu_reverse_vat",
              "construction_vat",
              "construction_reverse_vat",
            ].map((vatType) => (
              <div key={vatType}>
                <Ct.Spacer height={4} />
                <div>
                  {intl.formatMessage({
                    id: `office-company-settings.eu-construction-vat-accounts.${vatType}`,
                  })}
                </div>
                <Ct.Spacer height={1} />
                <Ct.Row>
                  <Select
                    intl={intl}
                    disabled={
                      accountsOptions(vatType as vatType)?.length === 0 ||
                      companyNotOnboarded
                    }
                    value={findSelectedOptionValue(vatType as vatType)}
                    options={accountsOptions(vatType as vatType) || []}
                    domain={"office-company-settings.vat-accounts"}
                    optionType={"buy-account"}
                    onChangeCallback={(selectedAccount) => {
                      const splittedLabel = selectedAccount.label.split(" ")
                      const number = splittedLabel[0]
                      const details = splittedLabel.slice(1).join(" ")
                      const account = {
                        id: Number(selectedAccount.value),
                        number: number,
                        details: details,
                      }

                      setAccounts({
                        ...accounts,
                        euVatAccount:
                          vatType === "eu_vat"
                            ? account
                            : accounts.euVatAccount,
                        euReverseVatAccount:
                          vatType === "eu_reverse_vat"
                            ? account
                            : accounts.euReverseVatAccount,
                        constructionVatAccount:
                          vatType === "construction_vat"
                            ? account
                            : accounts.constructionVatAccount,
                        constructionReverseVatAccount:
                          vatType === "construction_reverse_vat"
                            ? account
                            : accounts.constructionReverseVatAccount,
                      })
                    }}
                    error={
                      companyNotOnboarded
                        ? false
                        : vatType === "eu_vat"
                        ? accounts.euVatAccount === null
                        : vatType === "eu_reverse_vat"
                        ? accounts.euReverseVatAccount === null
                        : vatType === "construction_vat"
                        ? accounts.constructionVatAccount === null
                        : vatType === "construction_reverse_vat"
                        ? accounts.constructionReverseVatAccount === null
                        : true
                    }
                  />
                  <Ct.Spacer />
                  <Ct.Button
                    label={intl.formatMessage({
                      id: "office-company-settings.vat-accounts.create",
                    })}
                    onClick={() => {
                      setEuConstructionVatAccountToCreate({
                        vatType: vatType as vatType,
                        prefix: companySettings[vatType as vatType].prefix,
                        forbid: companySettings[vatType as vatType].forbid,
                      })
                      setIsModalDisplayed(true)
                    }}
                    width={22}
                    prefix={<Plus />}
                    disabled={companyNotOnboarded}
                  />
                  <Ct.Spacer />
                  <EditWrapper
                    onClick={() => {
                      setHistoryDisplay({
                        isDisplayed: true,
                        vatType: vatType as vatType,
                      })
                    }}
                  >
                    <StyledEye />
                  </EditWrapper>
                </Ct.Row>
              </div>
            ))}
            <Ct.Spacer />
          </>
          <Ct.Spacer height={1} />
        </BorderedContainer>
      </Container>
    </>
  )
}

const Container = styled.div`
  padding: 0 6rem;
`
const BorderedContainer = styled.div`
  border: 1px solid ${colors.cornflower};
  border-radius: 2.5rem;
  padding: 2rem 3rem;
  position: relative;
  display: flex;
  flex-direction: column;
`
const EditWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`
const StyledEye = styled(({ ...props }) => <Eye {...props} />)`
  & path {
    transition: all 0.2s ease-in-out;
    fill: ${colors.cornflower};
  }
  :hover {
    & path {
      transition: all 0.2s ease-in-out;
      fill: ${colors.lavender};
    }
  }
  cursor: pointer;
`
